import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { lifecycle } from 'recompose';
import { isEqual } from 'lodash';

import { Checkbox, FormFieldError } from 'shared/components';
import { confirmationCheck } from './confirmationCheck';

import theme from './FormStyles.scss';
import { checkErrorStatus, isErrorCode, checkSyncedErrorStatus } from './checkError';

export const handleCheckboxArray = (checked, valueToArray, { value, onChange }) => {
  if (!valueToArray) return onChange(checked);

  const arr = [...value];

  if (checked && !arr.includes(valueToArray)) {
    arr.push(valueToArray);
  } else {
    arr.splice(arr.indexOf(valueToArray), 1);
  }

  return onChange(arr);
};

const checkCheckboxState = (value, valueToArray) => {
  if (!valueToArray) return !!value;

  return value.includes(valueToArray);
};

const propTypes = {
  input: PropTypes.shape({
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    value: PropTypes.any,
    name: PropTypes.string,
  }).isRequired,
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    pristine: PropTypes.bool,
    submitFailed: PropTypes.bool,
  }).isRequired,
  errorMessagesMap: PropTypes.object,
  defaultValue: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  className: PropTypes.string,
  tooltipMessage: PropTypes.object,
  valueToArray: PropTypes.any,
  confirmation: PropTypes.bool,
  confirmationMessage: PropTypes.string,
  showTooltip: PropTypes.bool,
  tooltipType: PropTypes.oneOf(['text', 'hint', 'intl']),
  tooltipTestId: PropTypes.string,
  tooltipSize: PropTypes.string,
  tooltipPlacement: PropTypes.string,
  errorClassName: PropTypes.string,
  syncErrors: PropTypes.bool,
};

const defaultProps = {
  errorMessagesMap: {},
  defaultValue: '',
  label: null,
  className: '',
  tooltipMessage: null,
  valueToArray: null,
  confirmation: false,
  confirmationMessage: 'Action might affect another functionality',
  showTooltip: true,
  tooltipType: undefined,
  tooltipTestId: null,
  tooltipSize: 'medium',
  tooltipPlacement: 'topStart',
  errorClassName: propTypes.string,
  syncErrors: false,
};

export const CheckboxRedux = (props) => {
  const {
    input,
    meta: { touched, error },
    errorMessagesMap,
    valueToArray,
    confirmation,
    confirmationMessage,
    showTooltip,
    tooltipType,
    tooltipTestId,
    tooltipSize,
    tooltipPlacement,
    className,
    errorClassName,
    syncErrors,
    ...rest
  } = props;

  const errorMessage = !isErrorCode(error)
    ? { defaultMessage: error, id: 'default' }
    : error && errorMessagesMap[error];

  const errorStatus = syncErrors
    ? checkSyncedErrorStatus(props.meta)
    : checkErrorStatus(props.meta);

  return (
    <div className={ classnames(theme.root, className) }>
      <Checkbox
        name={ input.name }
        checked={ checkCheckboxState(input.value, valueToArray) }
        onClick={ (checked) => {
          confirmationCheck(
            confirmation,
            confirmationMessage,
            () => handleCheckboxArray(checked, valueToArray, input));
          input.onBlur();
        } }
        hasError={ touched && !!errorStatus }
        { ...rest }
      />
      <If condition={ showTooltip && errorStatus && errorMessage }>
        <FormFieldError
          tooltipType={ tooltipType }
          testId={ tooltipTestId }
          tooltipSize={ tooltipSize }
          tooltipMessage={ errorMessage }
          tooltipPlacement={ tooltipPlacement }
          className={ errorClassName }
        />
      </If>
    </div>
  );
};

CheckboxRedux.propTypes = propTypes;
CheckboxRedux.defaultProps = defaultProps;

export default lifecycle({
  shouldComponentUpdate(nextProps) {
    return !isEqual(this.props.input, nextProps.input)
      || !isEqual(this.props.meta, nextProps.meta)
      || this.props.disabled !== nextProps.disabled;
  },
})(CheckboxRedux);
